import { useState } from 'react'
import { ProTable } from '@ant-design/pro-components'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { Alert, App, Button, Input, Modal } from 'antd'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc.js'
import { ChevronDown, ListChecks } from 'lucide-react'
import type { SessionAccount } from '@super-mega/server/contracts/auth'
import { type RentalOrder } from '@super-mega/server/contracts/order'
import {
  getAuditFormValues,
  getAuditSearch,
  useMemberCreatorOptions,
  type AuditFormValues
} from '@/features/access/index.ts'
import { AuthorizedDropdown } from '@/features/authorization/authorized-dropdown.tsx'
import { Authorized } from '@/features/authorization/authorized.tsx'
import { OrderBillsDrawer } from '@/features/bill/index.ts'
import {
  OrderContractsDrawer,
  OrderContractStatusModal,
  type OrderContractStatusEditing
} from '@/features/contract/index.ts'
import { usePageTableNavigation } from '@/hooks/index.ts'
import type { PageTableNavigate } from '@/hooks/use-page-table.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getBatchJobAlertType, getBatchJobStatusLabel } from '@/utils/batch-job.ts'
import { getPositiveIntegerIdSearchValue } from '@/utils/list-query.ts'
import { orderBatchActionItems } from './const/batch-actions.ts'
import { useOrderListFilterOptions } from './hooks/use-order-list-filter-options.ts'
import { useRentalListActions } from './hooks/use-rental-list-actions.tsx'
import { OrderBatchUpdateModal, type OrderBatchEdit } from './order-batch-update-modal.tsx'
import { OrderListContainer } from './order-list-container.tsx'
import { OrderNotesEditModal } from './order-notes-edit-modal.tsx'
import { RentalForceDeleteModal } from './rental-force-delete-modal.tsx'
import { createRentalColumns } from './rental-list-columns.tsx'
import { DEFAULT_RENTAL_SEARCH, type RentalListSearch } from './rental-list-search.ts'

dayjs.extend(utc)

type Filters = AuditFormValues & {
  businessId?: number[]
  customerId?: number[]
  idSearch?: string
  keyword?: string
  note?: string
  status?: RentalListSearch['status']
  statusFilter?: RentalListSearch['status']
  archiveStatus?: RentalListSearch['archiveStatus']
  salespersonId?: number[]
  subjectId?: number[]
  subjectAssociation?: RentalListSearch['subjectAssociation']
  siteId?: number[]
}

export function RentalOrderList({
  search,
  navigateSearch,
  currentSession,
  compact = false,
  customerScoped = false,
  roomScoped = false,
  hideCreatedTimeFilter = false,
  defaultSearch = DEFAULT_RENTAL_SEARCH
}: {
  search: RentalListSearch
  navigateSearch: PageTableNavigate<RentalListSearch>
  currentSession: SessionAccount
  compact?: boolean
  hideCreatedTimeFilter?: boolean
  customerScoped?: boolean
  roomScoped?: boolean
  defaultSearch?: RentalListSearch
}) {
  const [contractsOrder, setContractsOrder] = useState<Pick<RentalOrder, 'id' | 'orderNo'>>()
  const [contractStatusEditing, setContractStatusEditing] = useState<OrderContractStatusEditing>()
  const [billsOrder, setBillsOrder] = useState<Pick<RentalOrder, 'id' | 'orderNo'>>()
  const trpc = useTRPC()
  const { message } = App.useApp()
  const [forceRemoving, setForceRemoving] = useState<RentalOrder>()
  const [batchForceRemoving, setBatchForceRemoving] = useState<RentalOrder[]>()
  const memberCreator = useMemberCreatorOptions('order.rental.read', search.creatorMemberId)
  const navigate = useNavigate({ from: '/orders/rentals/' })
  const [batchEditing, setBatchEditing] = useState<OrderBatchEdit>()
  const [editingNotes, setEditingNotes] = useState<RentalOrder>()
  const [archiving, setArchiving] = useState<RentalOrder>()
  const [archiveReason, setArchiveReason] = useState('')
  const canManageContracts =
    currentSession.isSuperAdmin ||
    currentSession.permissions.some(
      (permission) => permission.startsWith('order.rental.') && !permission.endsWith('.read')
    ) ||
    currentSession.permissions.some(
      (permission) => permission.startsWith('contract.document.') && !permission.endsWith('.read')
    )
  const canManage =
    currentSession.isSuperAdmin ||
    currentSession.permissions.some(
      (permission) => permission.startsWith('order.rental.') && !permission.endsWith('.read')
    )
  const canSubmit = currentSession.isSuperAdmin || currentSession.permissions.includes('order.rental.create')
  const canReadBills = currentSession.isSuperAdmin || currentSession.permissions.includes('bill.read')
  const query = useQuery({ ...trpc.order.rental.list.queryOptions(search), placeholderData: keepPreviousData })
  const {
    selectedIds,
    setSelection,
    batchRemove,
    batchBySearch,
    busy,
    searchBatchWarnings,
    searchBatchJobId,
    setSearchBatchJobId,
    searchBatchStatus,
    refreshBatchUpdate,
    confirmBatchRemove,
    confirmSearchBatch,
    confirmRemove,
    archive,
    invalidateRentalState,
    refreshAfterRemoval,
    openWorkspaceBySearch
  } = useRentalListActions({
    search,
    navigateSearch,
    currentSession,
    rows: query.data?.data,
    total: query.data?.total ?? 0
  })
  const selectedIdSet = new Set(selectedIds)
  const selectedOrders = (query.data?.data ?? []).filter((order) => selectedIdSet.has(order.id))
  const canBatchContract =
    selectedOrders.length > 0 &&
    selectedOrders.length <= 20 &&
    selectedOrders.every((order) => order.status === 'pending')
  const filterOptions = useOrderListFilterOptions({
    businessType: 'lease',
    includeSite: true,
    rows: query.data?.data
  })
  const editOrder = (order: RentalOrder) =>
    order.status !== 'archived'
      ? void navigate({
          to: '/orders/rentals/edit/$rentalId',
          params: { rentalId: String(order.id) },
          search: {},
          state: { rentalListReturnSearch: search }
        })
      : setEditingNotes(order)
  const openContract = (order: RentalOrder) => {
    setTimeout(() => {
      window.open(`/orders/rentals/contract/${order.id}`, '_blank')
    }, 100)
  }
  const openArchiveModal = (order: RentalOrder) => {
    setArchiveReason('')
    setArchiving(order)
  }
  const submitArchive = async () => {
    if (!archiving) {
      return
    }
    const reason = archiveReason.trim()
    if (!reason) {
      void message.error('请填写归档原因')
      return
    }
    try {
      await archive.mutateAsync({ rentalOrderId: archiving.id, reason })
      await invalidateRentalState()
      setArchiving(undefined)
      void message.success('租赁订单已归档')
    } catch (error) {
      void message.error(error instanceof Error ? error.message : '租赁订单归档失败')
    }
  }
  const table = usePageTableNavigation<RentalListSearch, Filters>({
    search,
    navigate: navigateSearch,
    defaultSearch,
    showTotal: (total) => `共 ${total} 个租赁订单`,
    getFormInitialValues: (value) => ({
      ...getAuditFormValues(value),
      businessId: value.businessId,
      customerId: value.customerId,
      idSearch: value.id === undefined ? undefined : String(value.id),
      keyword: value.keyword,
      note: value.note,
      salespersonId: value.salespersonId,
      statusFilter: value.status,
      archiveStatus: value.archiveStatus,
      subjectId: value.subjectId,
      subjectAssociation: value.subjectAssociation,
      siteId: value.siteId
    }),
    getSearchFromForm: (value) => ({
      ...getAuditSearch(value),
      businessId: value.businessId,
      customerId: value.customerId,
      salespersonId: value.salespersonId,
      subjectId: value.subjectAssociation === 'unlinked' ? undefined : value.subjectId,
      subjectAssociation: value.subjectAssociation,
      siteId: value.siteId,
      id: getPositiveIntegerIdSearchValue(value.idSearch),
      keyword: value.keyword?.trim() || undefined,
      note: value.note?.trim() || undefined,
      archiveStatus: value.archiveStatus,
      status: value.statusFilter
    })
  })
  const columns = createRentalColumns({
    search,
    memberCreator,
    filterOptions,
    canManage,
    canSubmit,
    canReadBills,
    isSuperAdmin: currentSession.isSuperAdmin,
    isPending: busy,
    onView: (order) =>
      void navigate({ to: '/orders/rentals/$rentalId', params: { rentalId: String(order.id) }, search }),
    onEdit: editOrder,
    onOpenContract: openContract,
    onArchive: openArchiveModal,
    onRemove: confirmRemove,
    onForceRemove: setForceRemoving,
    onContracts: setContractsOrder,
    onBills: setBillsOrder
  })
  let tablePersistenceKey = 'rental-orders:table'
  if (roomScoped) {
    tablePersistenceKey = 'room-rental-history:table'
  } else if (customerScoped) {
    tablePersistenceKey = 'customer-documents-rentals:table'
  } else if (compact) {
    tablePersistenceKey = 'order-group-rentals:table'
  }
  return (
    <OrderListContainer
      compact={compact}
      title="租赁订单"
      content="一张订单记录一段租期。每期租金、水电和押金通过账单分别管理。"
    >
      {contractsOrder && (
        <OrderContractsDrawer
          key={contractsOrder.id}
          order={{ type: 'rental', ...contractsOrder }}
          onClose={() => setContractsOrder(undefined)}
        />
      )}
      {contractStatusEditing && (
        <OrderContractStatusModal editing={contractStatusEditing} onClose={() => setContractStatusEditing(undefined)} />
      )}
      {billsOrder && (
        <OrderBillsDrawer order={{ type: 'rental', ...billsOrder }} onClose={() => setBillsOrder(undefined)} />
      )}
      {query.isError && (
        <Alert
          className="mb-4"
          type="error"
          showIcon
          title={query.error.message}
          action={<Button onClick={() => void query.refetch()}>重试</Button>}
        />
      )}
      {searchBatchWarnings.length > 0 && (
        <Alert
          className="mb-4"
          showIcon
          type="warning"
          title="部分文件需要人工清理"
          description={
            <ul>
              {[...new Set(searchBatchWarnings)].map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          }
        />
      )}
      {searchBatchJobId && searchBatchStatus.data && (
        <Alert
          className="mb-4"
          showIcon
          type={getBatchJobAlertType({
            state: searchBatchStatus.data.state,
            failedCount: searchBatchStatus.data.failedCount,
            warningCount: searchBatchStatus.data.warnings.length
          })}
          title={`按搜索批量任务：${getBatchJobStatusLabel(searchBatchStatus.data.state)}（${searchBatchStatus.data.processed}/${searchBatchStatus.data.total}）`}
          description={
            <>
              <div>{`成功 ${searchBatchStatus.data.successCount} 个，失败 ${searchBatchStatus.data.failedCount} 个。任务 ID：${searchBatchJobId}`}</div>
              <ul>
                {[...new Set(searchBatchStatus.data.warnings)].map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </>
          }
          action={
            <Button size="small" onClick={() => void refreshBatchUpdate()}>
              刷新列表
            </Button>
          }
        />
      )}
      {batchForceRemoving && (
        <RentalForceDeleteModal
          orders={batchForceRemoving}
          onClose={() => setBatchForceRemoving(undefined)}
          onDeleted={refreshAfterRemoval}
        />
      )}
      {forceRemoving && (
        <RentalForceDeleteModal
          key={forceRemoving.id}
          order={forceRemoving}
          onClose={() => setForceRemoving(undefined)}
          onDeleted={refreshAfterRemoval}
        />
      )}
      {editingNotes && (
        <OrderNotesEditModal order={editingNotes} kind="rental" onClose={() => setEditingNotes(undefined)} />
      )}
      <Modal
        open={Boolean(archiving)}
        title="归档租赁订单"
        onCancel={() => setArchiving(undefined)}
        onOk={() => void submitArchive()}
        confirmLoading={archive.isPending}
        okButtonProps={{ danger: true, disabled: !archiveReason.trim() }}
      >
        <p>归档保留订单、合同和账单；房号按结束租赁时确定的截止日期释放。</p>
        <label htmlFor="rental-list-archive-reason">归档原因</label>
        <Input.TextArea
          id="rental-list-archive-reason"
          value={archiveReason}
          onChange={(event) => setArchiveReason(event.target.value)}
          maxLength={5000}
          rows={4}
          placeholder="例如：企业列异、拖欠租金、租期届满等"
        />
      </Modal>
      <ProTable<RentalOrder, Filters>
        rowKey="id"
        rowSelection={
          canManage || canSubmit || canManageContracts
            ? {
                selectedRowKeys: selectedIds,
                onChange: (keys) => setSelection({ search, ids: keys.map(Number) }),
                getCheckboxProps: () => ({ disabled: busy || query.isFetching })
              }
            : false
        }
        columns={columns.filter(
          (column) =>
            (!customerScoped || column.dataIndex !== 'customerId') &&
            (!roomScoped || column.dataIndex !== 'siteId') &&
            (!hideCreatedTimeFilter || column.dataIndex !== 'createdAtRange')
        )}
        dataSource={query.data?.data ?? []}
        loading={query.isFetching}
        headerTitle={`租赁订单（${query.data?.total ?? 0}）`}
        columnsState={{
          persistenceKey: tablePersistenceKey,
          persistenceType: 'localStorage',
          defaultValue:
            compact && !roomScoped
              ? {
                  customerName: { show: false },
                  occupancyMode: { show: false },
                  leaseAction: { show: false },
                  updatedAt: { show: false }
                }
              : undefined
        }}
        formRef={table.formRef}
        form={{ initialValues: table.formInitialValues }}
        onSubmit={table.onSubmit}
        onReset={table.onReset}
        onChange={
          /* eslint-disable max-params -- Ant Design Table 的回调签名由组件定义。 */
          (_, __, sorter, extra) => {
            if (extra.action !== 'sort') {
              return
            }
            const sort = Array.isArray(sorter) ? sorter[0] : sorter
            void navigateSearch({
              replace: true,
              search: (previous) => ({
                ...previous,
                current: 1,
                sortField: 'createdAt',
                sortOrder: sort.order === 'ascend' ? 'asc' : 'desc'
              })
            })
          }
          /* eslint-enable max-params */
        }
        pagination={{ ...table.pagination, total: query.data?.total ?? 0 }}
        options={{ reload: () => void query.refetch() }}
        search={{ defaultCollapsed: compact, labelWidth: 'auto' }}
        scroll={{ x: '185rem' }}
        toolBarRender={() => [
          ...(canManage || canSubmit || canManageContracts
            ? [
                <AuthorizedDropdown
                  resource="order.rental"
                  key="batch-actions"
                  disabled={busy || query.isFetching || selectedIds.length === 0}
                  menu={{
                    items: [
                      ...(canManage
                        ? [
                            {
                              key: 'materials',
                              disabled: selectedIds.length > 20,
                              label: (
                                <Link
                                  to="/orders/rentals/materials/batch"
                                  search={{ ids: selectedIds.join(',') }}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  批量补齐资料
                                </Link>
                              )
                            }
                          ]
                        : []),
                      ...(orderBatchActionItems({
                        contractLabel: canBatchContract ? (
                          <Link
                            to="/orders/rentals/contracts/batch"
                            search={{ ids: selectedIds.join(',') }}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            批量生成合同
                          </Link>
                        ) : (
                          '批量生成合同'
                        ),
                        contractDisabled: !canBatchContract,
                        canContract: canSubmit || canManage,
                        canManageContracts,
                        canManage,
                        isOwner: currentSession.isSuperAdmin
                      }) ?? [])
                    ],
                    onClick: ({ key }) => {
                      if (key === 'contractStatus') {
                        setContractStatusEditing({
                          orderType: 'rental',
                          target: { mode: 'ids', ids: [...selectedIds] }
                        })
                        return
                      }
                      if (key === 'salesperson' || key === 'customer' || key === 'group') {
                        setBatchEditing({
                          kind: key,
                          orders: (query.data?.data ?? []).filter((order) => selectedIds.includes(order.id))
                        })
                        return
                      }
                      if (key === 'contract' || key === 'materials') {
                        return
                      }
                      if (key === 'forceDelete') {
                        const selected = new Set(selectedIds)
                        setBatchForceRemoving((query.data?.data ?? []).filter((order) => selected.has(order.id)))
                      } else {
                        confirmBatchRemove()
                      }
                    }
                  }}
                  placement="bottomRight"
                  trigger={['click']}
                >
                  <Button
                    disabled={busy || query.isFetching || selectedIds.length === 0}
                    loading={batchRemove.isPending}
                    icon={<ListChecks aria-hidden size={16} />}
                  >
                    批量操作 <ChevronDown aria-hidden size={14} />
                  </Button>
                </AuthorizedDropdown>
              ]
            : []),
          ...(canManage || canSubmit || canManageContracts
            ? [
                <AuthorizedDropdown
                  resource="order.rental"
                  key="search-batch-actions"
                  disabled={busy || query.isFetching || !query.data?.total}
                  menu={{
                    items: [
                      ...(canManage
                        ? [
                            {
                              key: 'materials',
                              label: '批量补齐资料'
                            }
                          ]
                        : []),
                      ...(orderBatchActionItems({
                        contractLabel: '批量生成合同',
                        contractDisabled: false,
                        canContract: canSubmit || canManage,
                        canManageContracts,
                        canManage,
                        isOwner: currentSession.isSuperAdmin
                      }) ?? [])
                    ],
                    onClick: ({ key }) => {
                      if (key === 'contractStatus') {
                        setContractStatusEditing({
                          orderType: 'rental',
                          target: { mode: 'search', search, expectedCount: query.data?.total ?? 0 }
                        })
                      } else if (key === 'salesperson' || key === 'customer' || key === 'group') {
                        setBatchEditing({ kind: key, search, expectedCount: query.data?.total ?? 0 })
                      } else if (key === 'contract') {
                        void openWorkspaceBySearch('contracts')
                      } else if (key === 'materials') {
                        void openWorkspaceBySearch('materials')
                      } else if (key === 'delete' || key === 'forceDelete') {
                        confirmSearchBatch(key)
                      }
                    }
                  }}
                >
                  <Button disabled={busy || query.isFetching || !query.data?.total} loading={batchBySearch.isPending}>
                    按搜索批量操作 <ChevronDown aria-hidden size={14} />
                  </Button>
                </AuthorizedDropdown>
              ]
            : []),
          ...(canSubmit && !roomScoped
            ? [
                <Authorized key="new" permissions={['order.rental.create']}>
                  <Button
                    key="new"
                    type="primary"
                    onClick={() =>
                      void navigate({
                        to: '/orders/rentals/new',
                        search: {},
                        state: { rentalListReturnSearch: search }
                      })
                    }
                  >
                    新建租赁订单
                  </Button>
                </Authorized>,
                <Authorized key="batch-new" permissions={['order.rental.create']}>
                  <Button
                    key="batch-new"
                    onClick={() =>
                      void navigate({
                        to: '/orders/rentals/batch-new',
                        search: { customerId: search.customerId?.length === 1 ? search.customerId[0] : undefined },
                        state: { rentalListReturnSearch: search }
                      })
                    }
                  >
                    批量创建租赁订单
                  </Button>
                </Authorized>
              ]
            : [])
        ]}
      />
      {batchEditing ? (
        <OrderBatchUpdateModal
          onQueued={setSearchBatchJobId}
          editing={batchEditing}
          orderKind="rental"
          onClose={() => setBatchEditing(undefined)}
          onSaved={refreshAfterRemoval}
        />
      ) : null}
    </OrderListContainer>
  )
}
